import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Invoice, InvoiceStatus, InvoiceStats } from 'shared';
import { invoiceApi } from '../api/invoices';
import { InvoiceStatsSummary } from './invoices/InvoiceStatsSummary';
import { InvoiceToolbar } from './invoices/InvoiceToolbar';
import { InvoiceDataTable } from './invoices/InvoiceDataTable';
import { GenerateInvoiceDialog } from './invoices/GenerateInvoiceDialog';
import { CustomInvoiceDialog } from './invoices/CustomInvoiceDialog';
import { PDFPreviewDialog } from './invoices/PDFPreviewDialog';
import { MarkAsPaidDialog } from './invoices/MarkAsPaidDialog';
import type { CreateCustomInvoiceRequest } from 'shared';

export const InvoiceManagement: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [stats, setStats] = useState<InvoiceStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<InvoiceStatus | 'ALL'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [creatingCustom, setCreatingCustom] = useState(false);
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);
  const [markPaidInvoice, setMarkPaidInvoice] = useState<Invoice | null>(null);
  const [markingPaid, setMarkingPaid] = useState(false);

  const toast = useRef<Toast>(null);

  useEffect(() => {
    loadInvoices();
    loadStats();
  }, [selectedYear, selectedStatus, searchQuery]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const result = await invoiceApi.list({
        year: selectedYear ?? undefined,
        status: selectedStatus === 'ALL' ? undefined : selectedStatus,
        search: searchQuery || undefined,
        page: 1,
        limit: 1000,
        sortBy: 'invoiceDate',
        sortOrder: 'desc',
      });
      setInvoices(result.invoices);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to load invoices',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await invoiceApi.getStats(selectedYear ?? new Date().getFullYear());
      setStats(statsData);
    } catch (error: any) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleGenerateAll = () => {
    setShowGenerateDialog(true);
  };

  const handleCreateCustom = async (data: CreateCustomInvoiceRequest) => {
    try {
      setCreatingCustom(true);
      await invoiceApi.createCustom(data);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Custom invoice created successfully',
        life: 3000,
      });
      setShowCustomDialog(false);
      loadInvoices();
      loadStats();
    } catch (error: any) {
      console.error('Failed to create custom invoice:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to create custom invoice',
        life: 5000,
      });
    } finally {
      setCreatingCustom(false);
    }
  };

  const handleGenerate = async (year: number) => {
    setGenerating(true);
    try {
      const result = await invoiceApi.generateDraftBatch(year);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Generated ${result.successful}/${result.total} invoices`,
        life: 5000,
      });

      if (result.failed.length > 0) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Warning',
          detail: `${result.failed.length} invoices failed to generate`,
          life: 5000,
        });
      }

      setShowGenerateDialog(false);
      if (year === selectedYear) {
        loadInvoices();
        loadStats();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to generate invoices',
        life: 5000,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleApproveSelected = () => {
    const draftInvoices = selectedInvoices.filter((inv) => inv.status === InvoiceStatus.DRAFT);

    if (draftInvoices.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No draft invoices selected',
        life: 3000,
      });
      return;
    }

    confirmDialog({
      message: `Are you sure you want to approve ${draftInvoices.length} invoice(s)?`,
      header: 'Confirm Approval',
      icon: 'pi pi-check-circle',
      accept: async () => {
        setLoading(true);
        try {
          const result = await invoiceApi.batchApprove(draftInvoices.map((inv) => inv.id));

          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: `Approved ${result.successful}/${result.total} invoices`,
            life: 5000,
          });

          setSelectedInvoices([]);
          loadInvoices();
          loadStats();
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Failed to approve invoices',
            life: 5000,
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleSendSelected = () => {
    const approvedInvoices = selectedInvoices.filter((inv) => inv.status === InvoiceStatus.APPROVED);

    if (approvedInvoices.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No approved invoices selected',
        life: 3000,
      });
      return;
    }

    confirmDialog({
      message: `Are you sure you want to send ${approvedInvoices.length} invoice(s) via email?`,
      header: 'Confirm Send',
      icon: 'pi pi-send',
      accept: async () => {
        setLoading(true);
        try {
          const result = await invoiceApi.batchSend(approvedInvoices.map((inv) => inv.id));

          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: `Sent ${result.successful}/${result.total} invoices`,
            life: 5000,
          });

          setSelectedInvoices([]);
          loadInvoices();
          loadStats();
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Failed to send invoices',
            life: 5000,
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleDeleteSelected = () => {
    const draftInvoices = selectedInvoices.filter((inv) => inv.status === InvoiceStatus.DRAFT);

    if (draftInvoices.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Only draft invoices can be deleted',
        life: 3000,
      });
      return;
    }

    confirmDialog({
      message: `Are you sure you want to delete ${draftInvoices.length} draft invoice(s)?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        setLoading(true);
        let successCount = 0;
        let failCount = 0;

        for (const invoice of draftInvoices) {
          try {
            await invoiceApi.delete(invoice.id);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: `Deleted ${successCount} invoice(s)`,
            life: 5000,
          });
        }

        if (failCount > 0) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to delete ${failCount} invoice(s)`,
            life: 5000,
          });
        }

        setSelectedInvoices([]);
        loadInvoices();
        loadStats();
        setLoading(false);
      },
    });
  };

  const handlePreview = async (invoice: Invoice) => {
    setPreviewInvoice(invoice);
    setShowPreviewDialog(true);

    try {
      const blobURL = await invoiceApi.getPreviewBlob(invoice.id);
      setPreviewURL(blobURL);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load PDF preview',
        life: 5000,
      });
      setShowPreviewDialog(false);
    }
  };

  const handleDownload = async (invoice: Invoice) => {
    try {
      await invoiceApi.downloadPDF(invoice.id, invoice.invoiceNumber);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'PDF downloaded successfully',
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to download PDF',
        life: 5000,
      });
    }
  };

  const handleApprove = async (invoice: Invoice) => {
    confirmDialog({
      message: `Approve invoice ${invoice.invoiceNumber}?`,
      header: 'Confirm Approval',
      icon: 'pi pi-check-circle',
      accept: async () => {
        try {
          await invoiceApi.approve(invoice.id);
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Invoice approved',
            life: 3000,
          });
          loadInvoices();
          loadStats();
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Failed to approve invoice',
            life: 5000,
          });
        }
      },
    });
  };

  const handleSend = async (invoice: Invoice) => {
    confirmDialog({
      message: `Send invoice ${invoice.invoiceNumber} to ${invoice.member?.email}?`,
      header: 'Confirm Send',
      icon: 'pi pi-send',
      accept: async () => {
        try {
          await invoiceApi.send(invoice.id);
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Invoice sent successfully',
            life: 3000,
          });
          loadInvoices();
          loadStats();
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Failed to send invoice',
            life: 5000,
          });
        }
      },
    });
  };

  const handleResend = async (invoice: Invoice) => {
    confirmDialog({
      message: `Resend invoice ${invoice.invoiceNumber} to ${invoice.member?.email}?`,
      header: 'Confirm Resend',
      icon: 'pi pi-refresh',
      accept: async () => {
        try {
          await invoiceApi.resend(invoice.id);
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Invoice resent successfully',
            life: 3000,
          });
          loadInvoices();
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Failed to resend invoice',
            life: 5000,
          });
        }
      },
    });
  };

  const handleMarkAsPaid = (invoice: Invoice) => {
    setMarkPaidInvoice(invoice);
    setShowMarkPaidDialog(true);
  };

  const handleMarkAsPaidConfirm = async (invoiceId: string, data: {
    receiveDate: string;
    paymentMethod: string;
    notes: string;
    totalReceived: number;
  }) => {
    try {
      setMarkingPaid(true);
      await invoiceApi.markAsPaid(invoiceId, data);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Invoice marked as paid',
        life: 3000,
      });
      setShowMarkPaidDialog(false);
      setMarkPaidInvoice(null);
      loadInvoices();
      loadStats();
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to mark invoice as paid',
        life: 5000,
      });
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleDelete = async (invoice: Invoice) => {
    confirmDialog({
      message: `Delete invoice ${invoice.invoiceNumber}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await invoiceApi.delete(invoice.id);
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Invoice deleted',
            life: 3000,
          });
          loadInvoices();
          loadStats();
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Failed to delete invoice',
            life: 5000,
          });
        }
      },
    });
  };

  return (
    <div data-component="invoice-management">
      <Toast ref={toast} />
      <ConfirmDialog />

      <InvoiceStatsSummary stats={stats} loading={loading} />

      <Card className="mt-3">
        <InvoiceToolbar
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          selectedInvoices={selectedInvoices}
          onGenerateAll={handleGenerateAll}
          onCreateCustom={() => setShowCustomDialog(true)}
          onApproveSelected={handleApproveSelected}
          onSendSelected={handleSendSelected}
          onDeleteSelected={handleDeleteSelected}
          loading={loading}
        />

        <InvoiceDataTable
          invoices={invoices}
          loading={loading}
          selectedInvoices={selectedInvoices}
          onSelectionChange={setSelectedInvoices}
          onPreview={handlePreview}
          onDownload={handleDownload}
          onApprove={handleApprove}
          onSend={handleSend}
          onResend={handleResend}
          onDelete={handleDelete}
          onMarkAsPaid={handleMarkAsPaid}
        />
      </Card>

      <GenerateInvoiceDialog
        visible={showGenerateDialog}
        onHide={() => setShowGenerateDialog(false)}
        onGenerate={handleGenerate}
        loading={generating}
      />

      <CustomInvoiceDialog
        visible={showCustomDialog}
        onHide={() => setShowCustomDialog(false)}
        onCreate={handleCreateCustom}
        loading={creatingCustom}
      />

      <MarkAsPaidDialog
        visible={showMarkPaidDialog}
        invoice={markPaidInvoice}
        onHide={() => {
          setShowMarkPaidDialog(false);
          setMarkPaidInvoice(null);
        }}
        onConfirm={handleMarkAsPaidConfirm}
        loading={markingPaid}
      />

      <PDFPreviewDialog
        visible={showPreviewDialog}
        invoice={previewInvoice}
        previewURL={previewURL}
        onHide={() => {
          setShowPreviewDialog(false);
          setPreviewInvoice(null);
          // Clean up blob URL to prevent memory leak
          if (previewURL) {
            window.URL.revokeObjectURL(previewURL);
          }
          setPreviewURL(null);
        }}
      />
    </div>
  );
};
